/** Stats summary row showing AVG, median, streak, trend */

import { useScrollOverflow } from '@hooks/useScrollOverflow';
import {
  computeChartSummary,
  computeStreak,
  PLAYER_SUMMARY_STAT_DEFS,
  STAT_SHORT_LABELS,
} from '@parlay-picker/shared/statsChart';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { Info } from 'lucide-react';
import { type ReactElement, useRef, useState } from 'react';
import type { ChartDataPoint } from './types';

interface StatsSummaryRowProps {
  chartData: ChartDataPoint[];
  propLine: number;
  isUnderBet: boolean;
  isAlternateLine: boolean;
  statKey: string;
}

export function StatsSummaryRow({
  chartData,
  propLine,
  isUnderBet,
  isAlternateLine,
  statKey,
}: StatsSummaryRowProps): ReactElement | null {
  const [showHelp, setShowHelp] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);
  const statsScrollRef = useRef<HTMLDivElement>(null);
  const { canScrollRight: statsOverflows } = useScrollOverflow(statsScrollRef);

  const played = chartData.filter((g) => !g.dnp);
  if (played.length === 0) return null;

  const values = played.map((g) => g.value);
  const { avg, median, trendDirection } = computeChartSummary(values, propLine);

  // Window-scoped streak: computed from displayed games against current line
  const { streak, streakType } = computeStreak(values, propLine, isAlternateLine);

  const shortLabel = STAT_SHORT_LABELS[statKey] || statKey;

  // Direction-aware colors: streak and trend respect bet direction
  const streakIsGood = isUnderBet ? streakType === 'under' : streakType === 'over';
  const trendIsGood = isUnderBet ? trendDirection === 'down' : trendDirection === 'up';

  return (
    <div className="relative">
      {statsOverflows && (
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-theme-surface to-transparent z-10 pointer-events-none" />
      )}
      <div
        ref={statsScrollRef}
        className="flex items-center gap-1 overflow-x-auto scrollbar-none py-1.5 -mx-1 px-1"
      >
        {/* AVG */}
        <div className="flex items-center gap-1 bg-theme-surface/60 rounded-md px-2 py-1 shrink-0">
          <span className="text-[10px] text-theme-text-tertiary uppercase tracking-wide">Avg</span>
          <span className="text-xs font-bold text-theme-text-secondary">{avg.toFixed(1)}</span>
        </div>
        {/* Median */}
        <div className="flex items-center gap-1 bg-theme-surface/60 rounded-md px-2 py-1 shrink-0">
          <span className="text-[10px] text-theme-text-tertiary uppercase tracking-wide">Med</span>
          <span className="text-xs font-bold text-theme-text-secondary">{median.toFixed(1)}</span>
        </div>
        {/* Streak */}
        <div className="flex items-center gap-1 bg-theme-surface/60 rounded-md px-2 py-1 shrink-0">
          <span className="text-[10px] text-theme-text-tertiary uppercase tracking-wide">
            Streak
          </span>
          <span
            className={cn(
              'text-xs font-bold',
              streakType === null
                ? 'text-theme-text-tertiary'
                : streakIsGood
                  ? 'text-theme-success'
                  : 'text-theme-danger',
            )}
          >
            {streak} {streakType ?? '—'}
          </span>
        </div>
        {/* Trend */}
        <div className="flex items-center gap-1 bg-theme-surface/60 rounded-md px-2 py-1 shrink-0">
          <span className="text-[10px] text-theme-text-tertiary uppercase tracking-wide">
            Trend
          </span>
          <span
            className={cn(
              'text-xs font-bold',
              trendDirection === 'flat'
                ? 'text-theme-text-tertiary'
                : trendIsGood
                  ? 'text-theme-success'
                  : 'text-theme-danger',
            )}
          >
            {trendDirection === 'up' ? '↑' : trendDirection === 'down' ? '↓' : '→'}
          </span>
        </div>
        {/* Avg vs Line */}
        <div className="flex items-center gap-1 bg-theme-surface/60 rounded-md px-2 py-1 shrink-0">
          <span className="text-[10px] text-theme-text-tertiary uppercase tracking-wide">
            {shortLabel}
          </span>
          <span
            className={cn(
              'text-xs font-bold',
              (isUnderBet ? avg < propLine : avg >= propLine)
                ? 'text-theme-success'
                : 'text-theme-danger',
            )}
          >
            {avg > propLine ? '+' : ''}
            {(avg - propLine).toFixed(1)}
          </span>
        </div>
        {/* Info icon */}
        <button
          type="button"
          onClick={() => setShowHelp((prev) => !prev)}
          className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-theme-text-tertiary hover:text-theme-text-secondary hover:bg-theme-surface-hover transition"
          aria-label="Explain stats"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Help tooltip — from shared PLAYER_SUMMARY_STAT_DEFS */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            ref={helpRef}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-theme-text-tertiary bg-theme-surface/40 rounded-md px-2.5 py-2 mt-1">
              {PLAYER_SUMMARY_STAT_DEFS.map((def) => (
                <div key={def.id} className={def.id === 'statVsLine' ? 'col-span-2' : ''}>
                  <span className="font-semibold text-theme-text-secondary">
                    {def.id === 'statVsLine' ? shortLabel : def.label}
                  </span>{' '}
                  — {def.description}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
